import { Panel, PanelType, ProgressIndicator, Stack } from "@fluentui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import { DisplayTermInfo } from "../../components/DisplayTermInfo";
import TermLogs from "../../components/TermLogs";
import { TermViewMenu } from "../../components/ViewMenu/TermsViewMenu";
import { ITerm, TermTypeEnum } from "../../interfaces/ITerm.interface";

import { ApprovalStep } from "@/components/Contracts/contract.interface";
import { ViewProgress } from "@/components/Contracts/ViewProgress";
import {
  CustomDrawer,
  DrawerActions,
  DrawerModules
} from "@/components/Shared/CustomDrawer";
import { MaritalStatusService } from "@/core/libs/api";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { FetchResponse } from "@/core/libs/api/utils/fetchData";
import { useConfirmModal } from "@/hooks/useConfirmModal";
import useQueryParams from "@/hooks/useQueryParams";
import { useSelectedBu } from "@/hooks/useSelectedBu";
import { EditAccounts } from "@/modules/Accounts/pages";
import { MaritalStatusTranslate } from "@/modules/Accounts/types";
import { handleTermRouteRedirection } from "@/modules/Terms/utils/handleTermRouteRedirection";

export const ViewTerm = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState(true);
  const [term, setTerm] = useState<ITerm>();
  const [steps, setSteps] = useState<ApprovalStep[]>();
  const [drawerState, setDrawerState] = useState<{
    action?: DrawerActions;
    isOpen: boolean;
  }>({
    action: null,
    isOpen: false
  });
  const [maritalStatusOptions, setMaritalStatusOptions] =
    useState<Array<{ key: number; text: string }>>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const navigate = useNavigate();
  const { selectedBU } = useSelectedBu();
  const { execute } = useApi();
  const { queryParams, changeQueryParams } = useQueryParams();
  const drawerContainerRef = useRef<HTMLDivElement>(null);
  const { openModalWithSettings } = useConfirmModal();
  const { id: termId } = useParams<{ id: string }>();

  const displayLogs = queryParams.occurrences === "true";

  const handleDisplayTermLogs = useCallback(
    (isOpen: boolean) => {
      changeQueryParams({ occurrences: isOpen ? "true" : undefined });
    },
    [changeQueryParams]
  );

  function openDrawer() {
    setDrawerState({ action: DrawerActions.EDIT, isOpen: true });
  }

  function closeDrawer() {
    setDrawerState({ action: null, isOpen: false });
  }

  const getTerm = useCallback(async () => {
    setIsLoading(true);

    const response = await execute<ITerm>({
      url: `terms/${termId}`,
      notificationMessage: `Buscando termo #${termId}`,
      params: { businessActionId: selectedBU?.id }
    });

    handleTermRouteRedirection({
      response,
      termId,
      navigate,
      expectedTermType: TermTypeEnum.ASSOCIATION,
      openModalWithSettings
    });

    const data = response.data;

    setCurrentStep(data?.currentStep?.order);
    setSteps(data?.approvalFlow?.steps);
    setTerm(data);
    setIsLoading(false);
  }, [navigate, execute, selectedBU?.id, openModalWithSettings, termId]);

  const handleMaritalStatusRequest = useCallback(async () => {
    const { signal } = new AbortController();
    const service = MaritalStatusService("", true);

    const result = await service.list({ params: {}, signal });

    setMaritalStatusOptions(
      result.data?.items?.map(item => ({
        key: item.id,
        text: MaritalStatusTranslate[item.name]
      }))
    );
  }, []);

  const editAccount = useCallback(
    async (values, _, closeDrawer): Promise<FetchResponse> => {
      const accountId = term?.account?.id;
      const res = await execute({
        url: `accounts/${accountId}`,
        method: "PATCH",
        params: { businessActionId: selectedBU?.id },
        bodyObj: values.body
      });

      if (res?.response.ok) {
        await getTerm();
      }

      if (!res?.response?.ok) {
        return res;
      }

      closeDrawer();

      return res;
    },
    [term?.account?.id, execute, selectedBU?.id, getTerm]
  );

  useEffect(() => {
    if (selectedBU) {
      setIsLoading(false);
    }
  }, [selectedBU]);

  useEffect(() => {
    void getTerm();
    void handleMaritalStatusRequest();
  }, [getTerm, handleMaritalStatusRequest]);

  return (
    <>
      <Helmet>
        <title>Visualização de termo - Libertas</title>
      </Helmet>

      {isLoading && (
        <ProgressIndicator
          styles={{
            root: { position: "absolute", left: 0, right: 0, top: 0 },
            itemProgress: { padding: 0 }
          }}
        />
      )}

      {term && (
        <Stack tokens={{ childrenGap: 15 }} style={{ position: "relative" }}>
          <TermViewMenu
            term={term}
            refetchTerm={getTerm}
            currentStep={term?.currentStep}
            setIsLoading={setIsLoading}
            handleDisplayTermLogs={handleDisplayTermLogs}
          />

          <div>
            {steps?.length > 0 && (
              <ViewProgress currentStep={currentStep} steps={steps} />
            )}
          </div>

          <DisplayTermInfo
            term={term}
            openDrawer={openDrawer}
            refetchTerm={getTerm}
          />
          <CustomDrawer
            isOpen={drawerState.isOpen}
            action={drawerState.action}
            module={DrawerModules.ACCOUNT}
            onCloseDrawer={closeDrawer}
            size="medium"
            drawerContainerRef={drawerContainerRef}
          >
            {drawerState.action === DrawerActions.EDIT && (
              <EditAccounts
                edit={editAccount}
                closeDrawer={closeDrawer}
                dismissMessage={undefined}
                drawerContainerRef={drawerContainerRef}
                message={null}
                addWhatsApp={true}
                selectedItem={String(term?.account?.id)}
                maritalStatusOptions={maritalStatusOptions}
                checkingAccountsRequired={false}
                isTerm={true}
              />
            )}
          </CustomDrawer>

          <Panel
            headerText="Histórico"
            isOpen={displayLogs}
            onDismiss={() => handleDisplayTermLogs(false)}
            type={PanelType.medium}
            closeButtonAriaLabel="Fechar"
            isLightDismiss
          >
            <TermLogs termId={term.id} />
          </Panel>
        </Stack>
      )}
    </>
  );
};
